/**
 * File hash calculation utilities using hash-wasm (WASM-based incremental SHA-256).
 * Supports files of any size by streaming chunks through the hasher,
 * never loading the entire file into memory.
 */

import { createSHA256 } from 'hash-wasm';

const HASH_CHUNK_SIZE = 64 * 1024 * 1024; // 64MB per chunk

/**
 * Calculate SHA-256 hash of a file with progress callback.
 * Memory usage: ~64MB regardless of file size.
 */
export async function calculateFileHashWithProgress(
  file: File,
  onProgress?: (progress: number) => void
): Promise<string> {
  if (!file || file.size === 0) {
    throw new Error('檔案無效或大小為 0');
  }

  const hasher = await createSHA256();
  hasher.init();

  let loaded = 0;

  for (let offset = 0; offset < file.size; offset += HASH_CHUNK_SIZE) {
    const end = Math.min(offset + HASH_CHUNK_SIZE, file.size);
    const chunk = file.slice(offset, end);
    const buffer = await chunk.arrayBuffer();
    hasher.update(new Uint8Array(buffer));

    loaded += buffer.byteLength;
    if (onProgress) {
      onProgress(Math.round((loaded / file.size) * 100));
    }
  }

  const hashHex = hasher.digest('hex');

  if (hashHex.length !== 64) {
    throw new Error(`Hash 長度錯誤: ${hashHex.length}, 預期 64`);
  }

  return hashHex;
}
